import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { EMPTY, Observable, from, of } from 'rxjs';
import { finalize, mergeMap, shareReplay, switchMap, tap } from 'rxjs/operators';
import { FileModel } from '../../../models';
import { Logger } from '../../../services/logger.service';
import type { FilePreviewData } from '../file-preview.component';
import { FilePreviewProcessor, PreviewProcessorKind, PreviewSourceEvent } from './file-preview-processor';

/**
 * Fetches the whole document as a `Blob`. Blobs are owned by the browser's blob storage
 * (disk-backed for large payloads)
 */
@Injectable()
export class BlobFetchProcessor implements FilePreviewProcessor, OnDestroy {
  readonly kind: PreviewProcessorKind = 'blob-fetch';

  private readonly _data = inject<FilePreviewData>(MAT_DIALOG_DATA);
  private readonly _http = inject(HttpClient);
  private readonly _logger = inject(Logger);

  /** Dedup concurrent fetches for the same file across renderer + download paths. */
  private readonly _inFlight = new Map<string, Observable<PreviewSourceEvent>>();

  // Per-instance cache name keeps entries scoped to this dialog and avoids cross-talk between
  // simultaneous previews. The cache is deleted in ngOnDestroy so disk usage doesn't accumulate.
  private readonly _cacheName = `file-preview-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  /** Universal fallback: any file the dialog can fetch (including unsupported-type downloads). */
  supports(): boolean {
    return true;
  }

  resolveSource(file: Partial<FileModel>): Observable<PreviewSourceEvent> {
    if (file.id == null) {
      // No id → not cacheable, so this is always a network fetch.
      return this.fetchBlob(file);
    }

    const key = typeof caches !== 'undefined' ? this._cacheKeyFor(file) : `direct:${file.id}`;

    const existing = this._inFlight.get(key);
    if (existing) return existing;

    const source =
      typeof caches !== 'undefined'
        ? from(this._readFromCache(key)).pipe(
            switchMap((cached) => {
              if (cached) {
                return of<PreviewSourceEvent>({ type: 'ready', source: { kind: 'blob', blob: cached }, cacheHit: true });
              }
              return this.fetchBlob(file).pipe(
                tap((event) => {
                  if (event.type === 'ready' && event.source.kind === 'blob') {
                    this._writeToCache(key, event.source.blob);
                  }
                }),
              );
            }),
          )
        : this.fetchBlob(file);

    const shared = source.pipe(
      finalize(() => this._inFlight.delete(key)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this._inFlight.set(key, shared);
    return shared;
  }

  getDownloadBlob(file: Partial<FileModel>): Observable<Blob> {
    return this.resolveSource(file).pipe(
      mergeMap((event) => (event.type === 'ready' && event.source.kind === 'blob' ? of(event.source.blob) : EMPTY)),
    );
  }

  private fetchBlob(file: Partial<FileModel>): Observable<PreviewSourceEvent> {
    return this._data.getFileUrl(file).pipe(
      switchMap((url) => this._http.get(url, { responseType: 'blob', observe: 'events', reportProgress: true })),
      mergeMap((event: HttpEvent<Blob>) => {
        if (event.type === HttpEventType.DownloadProgress) {
          return of<PreviewSourceEvent>({ type: 'progress', loadedBytes: event.loaded, totalBytes: event.total ?? null });
        }
        if (event.type === HttpEventType.Response && event.body) {
          return of<PreviewSourceEvent>({ type: 'ready', source: { kind: 'blob', blob: event.body }, cacheHit: false });
        }
        return EMPTY;
      }),
    );
  }

  private _cacheKeyFor(file: Partial<FileModel>): string {
    // Cache API keys must be valid URLs with an http/https scheme — custom schemes are
    // rejected. The hostname is bogus and unique so the key is never confusable with a
    // real network request and is easy to spot in DevTools' Cache Storage panel.
    return `https://file-preview-cache/${encodeURIComponent(String(file.id))}`;
  }

  private _readFromCache(key: string): Promise<Blob | null> {
    return (
      caches
        .open(this._cacheName)
        .then((cache) => cache.match(key))
        // blob() hands back a reference into blob storage — unlike arrayBuffer(), nothing is
        // materialized on the JS heap.
        .then((hit) => (hit ? hit.blob() : null))
        .catch((err) => {
          this._logger.warn('BlobFetchProcessor :: cache read failed, falling back to direct fetch', err);
          return null;
        })
    );
  }

  private _writeToCache(key: string, blob: Blob): Promise<void> {
    return caches
      .open(this._cacheName)
      .then((cache) => cache.put(key, new Response(blob)))
      .catch((err) => {
        // Best-effort write — a failure here just means the next read will miss and refetch.
        this._logger.warn('BlobFetchProcessor :: cache write failed', err);
      });
  }

  ngOnDestroy(): void {
    if (typeof caches !== 'undefined') {
      caches.delete(this._cacheName).catch((err) => {
        // Best-effort cleanup — orphan caches will be evicted by the browser eventually.
        this._logger.warn('BlobFetchProcessor :: cache cleanup failed', err);
      });
    }
  }
}
